import { StrictMode, useRef, useCallback } from "react";
import { createRoot } from "react-dom/client";
import GlassBox from "./GlassBox.jsx";
import { DEMO_SESSION, DEMO_AGENT_ID, SHELL_ENDPOINT } from "./demo_session_config.js";

// ─────────────────────────────────────────────────────────────────
// SESSION IDENTITY
// Generated once per page load.
// Passed to shell endpoint each turn so the backend can track
// continuity — even though each HTTP request is stateless.
// ─────────────────────────────────────────────────────────────────
const SESSION_ID = `demo_${Date.now().toString(36).toUpperCase()}_${
  Math.random().toString(36).slice(2, 6).toUpperCase()
}`;

// ─────────────────────────────────────────────────────────────────
// SHELL TURN API CALL
// POSTs to Wix /_functions/shell/turn.
// Returns canonical turn result consumed by GlassBox.
//
// turn_count is passed from GlassBox via onQuestion so the backend
// can seed session.turn_count correctly — preserving the 3-turn
// lifecycle across stateless HTTP requests.
// ─────────────────────────────────────────────────────────────────
async function callShellTurn({ question, model, turnCount }) {
  const body = {
    question,
    model: model,
    session_id:  SESSION_ID,
    agent_id:    DEMO_AGENT_ID,
    turn_count:  turnCount,
  };

  const res = await fetch(SHELL_ENDPOINT, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(body),
  });

  if (!res.ok) {
    // Surface HTTP errors with enough context for GlassBox error panel
    let detail = "";
    try { detail = await res.text(); } catch { /* ignore */ }
    throw new Error(`Shell endpoint error ${res.status}: ${detail.slice(0, 200)}`);
  }

  return res.json();
}

// ─────────────────────────────────────────────────────────────────
// APP ROOT
// Passes onQuestion handler to GlassBox.
// GlassBox calls onQuestion({ question, model, turnCount }) and
// awaits the canonical turn result.
// ─────────────────────────────────────────────────────────────────
function App() {
  // Track consecutive errors for graceful degradation messaging
  const errorCountRef = useRef(0);

  const handleQuestion = useCallback(async ({ question, model, turnCount }) => {
    try {
      const result = await callShellTurn({ question, model, turnCount });
      errorCountRef.current = 0; // reset on success
      return result;
    } catch (err) {
      errorCountRef.current += 1;
      console.error("[SDI Shell]", err.message);

      // Return a structured error result so GlassBox renders the error
      // panel rather than crashing. lifecycle_state: "ERROR" drives
      // the REJECT_RENDERED panel with failure.reason displayed.
      return {
        lifecycle_state:  "ERROR",
        commit_decision:  "REJECT",
        turn_count:       turnCount,
        session_id:       SESSION_ID,
        agent_id:         DEMO_AGENT_ID,
        model_provider:   model,
        question,
        answer:           null,
        governed_answer:  null,
        der:              null,
        compile_result:   null,
        ledger_result:    { written: false },
        chain_verify:     null,
        failure: {
          reason:         err.message,
          compile_errors: [],
          is_stop_der:    false,
        },
      };
    }
  }, []);

  return (
    <GlassBox
      session={DEMO_SESSION}
      onQuestion={handleQuestion}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// MOUNT
// ─────────────────────────────────────────────────────────────────
createRoot(document.getElementById("root")).render(
  <StrictMode>
    <App />
  </StrictMode>
);
